import { ApolloCache } from '@apollo/client'
import { v4 as uuidv4 } from 'uuid'

import {
    Attachment,
    AttachmentType,
    ChatFieldsFragment,
    ChatMessageFieldsFragment,
    ClientPhoneFieldsFragment,
    GetChatMessagesByChatIdDocument,
    GetChatMessagesByChatIdQuery,
    GetChatMessagesByChatIdQueryVariables,
    MessageMethod,
    MessageOrigin,
    MessageType,
} from '~graphql/generated/graphql'

export const OPTIMISTIC_MESSAGE_ID_PREFIX = 'optimistic-'

export function isOptimisticMessageId(id: string): boolean {
    return id.startsWith(OPTIMISTIC_MESSAGE_ID_PREFIX)
}

type OptimisticSender = {
    id: string
    first_name?: string | null
    last_name?: string | null
}

type BuildOptimisticMessageInput = {
    chat: ChatFieldsFragment
    text: string
    attachments: Attachment[]
    messageMethods: MessageMethod[]
    patientId?: string
    patientName?: string | null
    clinicUser?: OptimisticSender
    clientPhone?: ClientPhoneFieldsFragment
    hiddenForClient: boolean
}

export function buildOptimisticChatMessage({
    chat,
    text,
    attachments,
    messageMethods,
    patientId,
    patientName,
    clinicUser,
    clientPhone,
    hiddenForClient,
}: BuildOptimisticMessageInput): ChatMessageFieldsFragment {
    const now = Date.now()
    const patient = patientId
        ? {
              __typename: 'Patient' as const,
              id: patientId,
              name: patientName ?? null,
              pms_id: null,
          }
        : null

    return {
        __typename: 'Message',
        id: `${OPTIMISTIC_MESSAGE_ID_PREFIX}${uuidv4()}`,
        client: null,
        chat: {
            __typename: 'Chat',
            id: chat.id,
            clinic: chat.clinic,
        },
        patient,
        patients: patient ? [patient] : [],
        clinic_user: clinicUser
            ? {
                  __typename: 'User',
                  id: clinicUser.id,
                  first_name: clinicUser.first_name ?? null,
                  last_name: clinicUser.last_name ?? null,
              }
            : null,
        notification: null,
        bulk_message: null,
        text,
        email_text: null,
        postcard_template: null,
        attachments: attachments.map(attachment => ({
            __typename: 'Attachment' as const,
            id: attachment.id,
            url: attachment.url,
            filename: attachment.filename,
            type: attachment.type ?? AttachmentType.Image,
        })),
        client_phone: clientPhone
            ? {
                  __typename: 'ClientPhone' as const,
                  ...clientPhone,
              }
            : null,
        message_notification_templates: null,
        message_methods: messageMethods,
        message_type: MessageType.User,
        message_origin: MessageOrigin.ChckupDashboard,
        hidden_for_client: hiddenForClient,
        cta_text: null,
        cta_url: null,
        email_error: null,
        sms_error: null,
        push_error: null,
        postcard_error: null,
        other_errors: null,
        is_preview: false,
        written_back_at: null,
        template_data: null,
        created_at: now,
        updated_at: now,
    }
}

export function insertMessageIntoChatQuery(
    cache: ApolloCache<object>,
    variables: GetChatMessagesByChatIdQueryVariables,
    message: ChatMessageFieldsFragment,
    replaceOptimistic = false,
): void {
    cache.updateQuery<
        GetChatMessagesByChatIdQuery,
        GetChatMessagesByChatIdQueryVariables
    >(
        {
            query: GetChatMessagesByChatIdDocument,
            variables,
        },
        existing => {
            const current = existing?.getChatMessagesByChatId ?? []
            const withoutDuplicate = current.filter(existingMessage => {
                if (existingMessage.id === message.id) return false
                if (
                    replaceOptimistic &&
                    isOptimisticMessageId(existingMessage.id)
                ) {
                    return false
                }
                return true
            })

            return {
                getChatMessagesByChatId: [message, ...withoutDuplicate],
            }
        },
    )
}

export function removeOptimisticMessagesFromChatQuery(
    cache: ApolloCache<object>,
    variables: GetChatMessagesByChatIdQueryVariables,
): void {
    cache.updateQuery<
        GetChatMessagesByChatIdQuery,
        GetChatMessagesByChatIdQueryVariables
    >(
        {
            query: GetChatMessagesByChatIdDocument,
            variables,
        },
        existing => {
            if (!existing?.getChatMessagesByChatId) return existing

            return {
                getChatMessagesByChatId:
                    existing.getChatMessagesByChatId.filter(
                        message => !isOptimisticMessageId(message.id),
                    ),
            }
        },
    )
}
